import type { ReactNode } from 'react';
import { cn } from '@herit-fe/heritage-ui';

export interface OptionCardProps {
  /** 좌측 슬롯 — 이미지/아이콘/상태 아바타. */
  leading?: ReactNode;
  /** 중앙 본문 — 제목·부제·지표 등(호출처 조립). */
  children: ReactNode;
  /** 우측 슬롯 — 선택 표시(Check·Radio dot)·메타 라벨. */
  trailing?: ReactNode;
  /** 선택 상태 — primary 테두리 + 틴트. */
  selected?: boolean;
  /** 비활성(흐림) — 상호작용 차단은 감싼 `button`/`label` 이 소유(여기는 표현만). */
  disabled?: boolean;
  className?: string;
}

/**
 * 선택 카드 프리미티브 — 테두리 카드에 **좌(leading) · 중앙(본문) · 우(trailing/선택표시)** 슬롯. **순수 표현**
 * (클릭·포커스·disabled 동작은 감싼 `button`/`label`/`RadioGroupItem` 이 소유). "좌 이미지/아이콘 ~ 중앙 본문 ~
 * 우 선택" 구조를 공유하는 SSOT — 발전소 선택 시트 행·인버터 단일선택 카드 등이 재사용한다. 선택=primary
 * 테두리/틴트, hover=muted(선택·비활성 제외).
 */
export function OptionCard({
  leading,
  children,
  trailing,
  selected,
  disabled,
  className,
}: OptionCardProps) {
  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border bg-card',
        !selected && !disabled && 'hover:bg-muted/50',
        disabled && 'opacity-50',
        className,
      )}
    >
      {leading != null ? <span className="shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1">{children}</span>
      {trailing != null ? <span className="shrink-0">{trailing}</span> : null}
    </div>
  );
}
